import { useEffect, useRef } from "react";
import { siteConfig } from "@/config/site";

type JsonLdObject = Record<string, unknown>;

function injectScript(dataAttr: string, json: JsonLdObject): void {
  const removed = document.querySelector(`script[data-ld="${dataAttr}"]`);
  if (removed) removed.remove();

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-ld", dataAttr);
  script.textContent = JSON.stringify(json);
  document.head.appendChild(script);
}

function removeScript(dataAttr: string): void {
  const existing = document.querySelector(`script[data-ld="${dataAttr}"]`);
  if (existing) existing.remove();
}

interface JsonLdInjectorProps {
  dataAttr: string;
  json: JsonLdObject | null;
}

function JsonLdInjector({ dataAttr, json }: JsonLdInjectorProps) {
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!json) return;

    const timer = setTimeout(() => {
      injectScript(dataAttr, json);
      mountedRef.current = true;
    }, 0);

    return () => {
      clearTimeout(timer);
      if (mountedRef.current) {
        removeScript(dataAttr);
        mountedRef.current = false;
      }
    };
  }, [dataAttr, json]);

  return null;
}

/* ───────── Organization (site-wide in index.html; kept for reference) ───────── */

/* ───────── WebPage (home) ───────── */
export function HomeWebPageJsonLd() {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": siteConfig.absoluteUrl("/#webpage"),
    url: siteConfig.absoluteUrl("/"),
    name: `${siteConfig.brandName}｜青城十九俠系列與大華文苑書系`,
    description:
      "千華駐科技以青城十九俠系列與大華文苑書系為主要出版方向。本品牌委託崧博出版經銷，官方網站由崧博出版製作。",
    inLanguage: siteConfig.inLanguage,
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    about: { "@id": `${siteConfig.url}/#organization` },
  };

  return <JsonLdInjector dataAttr="webpage-home" json={json} />;
}

/* ───────── AboutPage ───────── */
export function AboutPageJsonLd() {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": siteConfig.absoluteUrl("/about#webpage"),
    url: siteConfig.absoluteUrl("/about"),
    name: `關於${siteConfig.brandName}`,
    description:
      "了解千華駐科技的品牌定位、青城十九俠系列與大華文苑書系，以及主要出版方向。",
    inLanguage: siteConfig.inLanguage,
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    about: { "@id": `${siteConfig.url}/#organization` },
    breadcrumb: { "@id": siteConfig.absoluteUrl("/about#breadcrumb") },
  };

  return <JsonLdInjector dataAttr="webpage-about" json={json} />;
}

export function AboutBreadcrumbJsonLd() {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": siteConfig.absoluteUrl("/about#breadcrumb"),
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首頁",
        item: siteConfig.absoluteUrl("/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "品牌介紹",
        item: siteConfig.absoluteUrl("/about"),
      },
    ],
  };

  return <JsonLdInjector dataAttr="breadcrumb-about" json={json} />;
}

/* ───────── CollectionPage + ItemList (stores) ───────── */
interface StoreItem {
  name: string;
  url: string;
}

export function StoresCollectionPageJsonLd({ stores }: { stores: StoreItem[] }) {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": siteConfig.absoluteUrl("/stores#webpage"),
    url: siteConfig.absoluteUrl("/stores"),
    name: `${siteConfig.brandName}合作購書通路`,
    description:
      "查找千華駐科技《青城十九俠》及大華文苑書系的合作購書通路與正式書目查詢方式。",
    inLanguage: siteConfig.inLanguage,
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    about: { "@id": `${siteConfig.url}/#organization` },
    breadcrumb: { "@id": siteConfig.absoluteUrl("/stores#breadcrumb") },
    mainEntity: { "@id": siteConfig.absoluteUrl("/stores#itemlist") },
  };

  return <JsonLdInjector dataAttr="webpage-stores" json={json} />;
}

export function StoresItemListJsonLd({ stores }: { stores: StoreItem[] }) {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": siteConfig.absoluteUrl("/stores#itemlist"),
    name: `${siteConfig.brandName}合作購書通路`,
    numberOfItems: stores.length,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: stores.map((store, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: store.name,
      url: store.url,
    })),
  };

  return <JsonLdInjector dataAttr="itemlist-stores" json={json} />;
}

export function StoresBreadcrumbJsonLd() {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": siteConfig.absoluteUrl("/stores#breadcrumb"),
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首頁",
        item: siteConfig.absoluteUrl("/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "合作購書通路",
        item: siteConfig.absoluteUrl("/stores"),
      },
    ],
  };

  return <JsonLdInjector dataAttr="breadcrumb-stores" json={json} />;
}

/* ───────── FAQPage ───────── */
interface FaqItem {
  q: string;
  a: string;
}

export function FaqPageJsonLd({ faqs }: { faqs: FaqItem[] }) {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": siteConfig.absoluteUrl("/faq#webpage"),
    url: siteConfig.absoluteUrl("/faq"),
    name: `${siteConfig.brandName}常見問題`,
    inLanguage: siteConfig.inLanguage,
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    breadcrumb: { "@id": siteConfig.absoluteUrl("/faq#breadcrumb") },
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a.replace(/\n/g, " "),
      },
    })),
  };

  return <JsonLdInjector dataAttr="webpage-faq" json={json} />;
}

export function FaqBreadcrumbJsonLd() {
  const json: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": siteConfig.absoluteUrl("/faq#breadcrumb"),
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首頁",
        item: siteConfig.absoluteUrl("/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "常見問題",
        item: siteConfig.absoluteUrl("/faq"),
      },
    ],
  };

  return <JsonLdInjector dataAttr="breadcrumb-faq" json={json} />;
}